import { useEffect, useState } from "react";
import Pagination from "../../components/Pagination/Pagination";
import FilterBar, { FilterField } from "../../components/FilterBar";
import { activityService } from "../../services/apiService";
import { formatDateTime } from "../../utils/date";

const ACTION_BADGE = {
  create: "bg-success",
  update: "bg-primary",
  delete: "bg-danger",
  approve: "bg-success",
  reject: "bg-secondary",
  login: "bg-info text-dark",
  permissions_updated: "bg-warning text-dark",
};

const ACTION_FILTERS = [
  { id: "", label: "All actions" },
  { id: "create", label: "Create" },
  { id: "update", label: "Update" },
  { id: "delete", label: "Delete" },
  { id: "approve", label: "Approve" },
  { id: "reject", label: "Reject" },
  { id: "login", label: "Login" },
  { id: "permissions_updated", label: "Permissions updated" },
];

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0 });

  const load = (params = {}) => {
    setLoading(true);
    const body = {
      page: params.page ?? pagination.page,
      limit: params.limit ?? pagination.limit,
      search: params.search ?? search,
      action: params.action ?? actionFilter,
    };
    Promise.all([
      activityService.getAll(body),
      activityService.getCount(body),
    ])
      .then(([listRes, countRes]) => {
        setRows(listRes?.data || []);
        setPagination((p) => ({
          ...p,
          page: body.page,
          limit: body.limit,
          total: countRes?.data?.count ?? 0,
        }));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load({ page: 1, action: actionFilter, search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    load({ page: 1, search, action: actionFilter });
  };

  const onReset = (e) => {
    e.preventDefault();
    setActionFilter("");
    setSearch("");
    load({ page: 1, action: "", search: "" });
  };

  const handleActionChange = (e) => {
    setActionFilter(e.target.value);
    setSearch("");
  };

  const emptyMessage = actionFilter
    ? `No ${actionFilter.replace(/_/g, " ")} activity`
    : "No activity";

  return (
    <div className="card mb-4">
      <div className="card-header">
        <strong>Activity Log</strong>
      </div>
      <div className="card-body">
        <FilterBar onSubmit={onSearch} onReset={onReset}>
          <FilterField id="activity-action-filter" label="Action">
            <select
              id="activity-action-filter"
              className="form-select"
              value={actionFilter}
              onChange={handleActionChange}
            >
              {ACTION_FILTERS.map((f) => (
                <option key={f.id || "all"} value={f.id}>{f.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField id="activity-search" label="Search">
            <input
              id="activity-search"
              className="form-control"
              placeholder="User, module, description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterField>
        </FilterBar>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Action</th>
                <th>Module</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={5} className="text-center py-4">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="text-center py-4 text-muted">{emptyMessage}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="small text-muted">{formatDateTime(r.created_at)}</td>
                    <td>{r.user_name}</td>
                    <td>
                      <span className={`badge ${ACTION_BADGE[r.action] || "bg-secondary"}`}>
                        {r.action}
                      </span>
                    </td>
                    <td>{r.module}</td>
                    <td className="small">{r.description}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          itemsCount={rows.length}
          loading={loading}
          onPageChange={(page) => load({ page, search, action: actionFilter })}
          onLimitChange={(limit) => load({ page: 1, limit, search, action: actionFilter })}
        />
      </div>
    </div>
  );
}
